import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import ResizableRect from 'react-resizable-rotatable-draggable'
import AutosizeInput from 'react-input-autosize'
import ColorPicker from '../../../utilities/colorTools/ColorPicker'
import FontFamilyDropdown from './FontFamilyDropdown'
import FontSizeDropdown from './FontSizeDropdown'
import FontFormatsTool from './FontFormatsTool'
//MUI
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
//Redux
import { useSelector } from 'react-redux'

const Textbox = (props) => {
  const { textbox, updateTextbox, deleteTextbox } = props

  const TIMES_ROMAN = 'Times Roman'
  const COURIER = 'Courier'
  const FONT_MULTIPLIER = 1.333333333333333
  const MINIMUM_HEIGHT = 10
  const MINIMUM_WIDTH = 10

  const page = useSelector((state) => state.document.page)

  const [isDragging, setIsDragging] = useState(false)
  const [formats, setFormats] = useState([])
  const [prevFontSize, setPrevFontSize] = useState(20)
  const [prevFontFamily, setPrevFontFamily] = useState(COURIER)
  const [isToolVisible, setIsToolVisible] = useState(false)
  const [borderStyle, setBorderStyle] = useState('solid')
  const [padding, setPadding] = useState('0px')

  var visibilityProps = useMemo(() => {
    return {
      opacity: isToolVisible ? 1 : 0,
      visibility: isToolVisible ? 'visible' : 'hidden',
    }
  }, [isToolVisible])

  useEffect(() => {
    setPrevFontSize(textbox.fontSize)
  }, [textbox.fontSize]) //to re-render textbox after changing fontsize

  useEffect(() => {
    setPrevFontFamily(textbox.fontFamily)
  }, [textbox.fontFamily]) //to re-render textbox after changing font family

  useEffect(() => {
    if (page.pageNumber === textbox.pageNumber) {
      updateTextbox({
        ...textbox,
        offsetLeft: page.offsetLeft,
        offsetTop: page.offsetTop,
        multiplier: page.multiplier,
        pageHeight: page.pageHeight,
        pageWidth: page.pageWidth,
        ...(textbox.tbLeft < page.offsetLeft && {
          tbLeft: page.offsetLeft,
        }),
        ...(textbox.tbTop < page.offsetTop && { tbTop: page.offsetTop }),
        ...(textbox.tbLeft + textbox.tbWidth > page.offsetLeft + page.pageWidth && {
          tbLeft: page.offsetLeft + page.pageWidth * 0.75,
        }),
        ...(textbox.tbTop + textbox.tbHeight > page.offsetTop + page.pageHeight && {
          tbTop: page.offsetTop + page.pageHeight * 0.75,
        }),
      })
    }
  }, [page])

  const contentStyle = useMemo(() => {
    return {
      width: textbox.fontSize * 5,
      height: textbox.tbHeight,
      top: textbox.tbTop,
      left: textbox.tbLeft,
      position: 'absolute',
      userSelect: 'none',
    }
  }, [textbox])

  const textToolBarStyle = useMemo(() => {
    return {
      top: textbox.tbTop - textbox.tbHeight * 1.1,
      left: textbox.tbLeft,
      position: 'absolute',
      userSelect: 'none',
      zIndex: 1,
      border: '1px solid black',
      display: 'inline-flex',
      ...visibilityProps,
      backgroundColor: 'white',
      transition: 'visibility 0s, opacity .35s ease-in-out',
    }
  }, [textbox, visibilityProps])

  const textToolItemStyle = useMemo(() => {
    return {
      minheight: '15px',
      maxWidth: '150px',
      fontSize: 'small',
      padding: '0px 7px',
    }
  }, [])

  const getColor = (color) => {
    updateTextbox({ ...textbox, color })
  }
  const getFontSize = (fontSize) => {
    updateTextbox({ ...textbox, fontSize })
  }
  const getFontFamily = (fontFamily) => {
    var fontStyle = 'Normal'
    if (textbox.fontStyle !== 'Normal') {
      if (fontFamily === 'Times Roman') fontStyle = 'Italic'
      else fontStyle = 'Oblique'
    }
    updateTextbox({ ...textbox, fontFamily, fontStyle })
  }

  const getFormat = (format) => {
    setFormats(format)
    const isBold = format.includes('bold') ? 'Bold' : 'Normal'
    const isSlanted = format.includes('slanted') ? (textbox.fontFamily === TIMES_ROMAN ? 'Italic' : 'Oblique') : 'Normal'
    updateTextbox({
      ...textbox,
      fontWeight: isBold,
      fontStyle: isSlanted,
    })
  }

  const handleDrag = (deltaX, deltaY) => {
    if (
      textbox.tbLeft + deltaX >= textbox.offsetLeft &&
      textbox.tbLeft + deltaX <= textbox.offsetLeft + textbox.pageWidth - textbox.tbWidth && // handle horizontal dragging
      textbox.tbTop + deltaY >= textbox.offsetTop &&
      textbox.tbTop + deltaY <= textbox.offsetTop + textbox.pageHeight - textbox.fontSize * FONT_MULTIPLIER * FONT_MULTIPLIER
    ) {
      // handle verticle dragging
      updateTextbox({
        ...textbox,
        tbLeft: textbox.tbLeft + deltaX,
        tbTop: textbox.tbTop + deltaY,
      })
    }
  }

  const handleMouseEnter = () => {
    setIsToolVisible(true)
    setBorderStyle('solid')
    setPadding('0px')
  }

  const handleMouseLeave = () => {
    setIsToolVisible(false)
    setBorderStyle('none')
    setPadding('1px')
  }

  return (
    <div
      onMouseLeave={handleMouseLeave}
      style={page.pageNumber === textbox.pageNumber ? { display: 'inline' } : { display: 'none' }}
    >
      {isDragging ? null : (
        <div style={textToolBarStyle}>
          <div style={textToolItemStyle}>
            <FontFamilyDropdown getFontFamily={getFontFamily} fontFamily={textbox.fontFamily} />
          </div>
          <div style={textToolItemStyle}>
            <FontSizeDropdown getFontSize={getFontSize} fontSize={textbox.fontSize} />
          </div>
          <div style={textToolItemStyle}>
            <ColorPicker getColor={getColor} color={textbox.color} />
          </div>
          <div style={textToolItemStyle}>
            <FontFormatsTool getFormat={getFormat} formats={formats} />
          </div>
          <div
            style={textToolItemStyle}
            onClick={() => {
              deleteTextbox(textbox.id)
            }}
          >
            <HighlightOffIcon
              style={{
                backgroundColor: 'red',
                color: 'white',
                borderRadius: '50%',
                cursor: 'pointer',
              }}
            />
          </div>
        </div>
      )}
      <>
        {prevFontFamily === textbox.fontFamily && prevFontSize === textbox.fontSize ? ( // re-render textbox to avoid buggy textbox
          <div style={contentStyle} onMouseEnter={handleMouseEnter}>
            <AutosizeInput
              name='form-field-name'
              value={textbox.value}
              placeholder='Your texts'
              placeholderIsMinWidth={true}
              minWidth={textbox.tbWidth}
              autoComplete='off'
              inputStyle={{
                fontStyle: textbox.fontStyle,
                fontWeight: textbox.fontWeight,
                fontSize: textbox.fontSize,
                fontFamily: textbox.fontFamily,
                color: `rgb(${textbox.color.r}, ${textbox.color.g}, ${textbox.color.b})`,
                backgroundColor: 'transparent',
                borderWidth: '1px',
                borderStyle,
                padding,
              }}
              onChange={(e) => updateTextbox({ ...textbox, value: e.target.value })}
            />
          </div>
        ) : null}

        <ResizableRect
          top={textbox.tbTop}
          left={textbox.tbLeft}
          aspectRatio
          minWidth={MINIMUM_WIDTH}
          width={20}
          minHeight={MINIMUM_HEIGHT}
          height={textbox.tbHeight}
          onDrag={handleDrag}
          onDragStart={() => {
            setIsDragging(true)
          }}
          onDragEnd={() => {
            setIsDragging(false)
          }}
          style={{ zIndex: 2, overflow: 'hidden' }}
          className='cursor-move'
        />
      </>
    </div>
  )
}

Textbox.propTypes = {
  textbox: PropTypes.object.isRequired,
  updateTextbox: PropTypes.func.isRequired,
  deleteTextbox: PropTypes.func.isRequired,
}

export default Textbox
